<template>
  <b-container fluid class="pt-3 px-5 flex-grow-1 d-flex flex-column">

    <!-- top row -->
    <b-row>
      <b-col cols="12" class="pt-1">
        <riskhub-vue-element-loading :active="loading" :is-full-screen="true" />
        <b-row class="d-flex px-3">
          <b-col class="col-sm-2 px-0 pr-xl-5" cols="12">
            <stat-box label="Total"
                      :value="totalAssets"
                      color="#00D2B4"
                      size="large"
            >
              {{ totalAssets }}
            </stat-box>
            <div class="divider d-none d-xl-block"></div>
          </b-col>
          <b-col class="group group-1 px-0 pr-xl-5">
            <breakdown-panel v-show="!loading"
                             class="mt-2"
                             :data="breakdownPanelData"
                             label-class="label-medium text-uppercase"
                             :checkbox="true"
                             :checkbox-model="enabledTypes"
                             @update:checkboxModel="updateEnabledTypes($event)"
            />
          </b-col>
        </b-row>
      </b-col>
    </b-row>
    <!-- bottom row -->
    <b-row class="my-5">
      <b-col cols="12" class="pt-1">
        <riskhub-vue-element-loading :active="loading" :is-full-screen="true" />
        <b-row class="d-flex px-3">
          <b-col>
            <donut-chart-panel v-if="!loading"
                               :chart-data="chartData"
                               :chart-colors="chartColors"
                               :chart-options="chartOptions"
                               ref="chartPanel"
            />
          </b-col>
        </b-row>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
import StatBox from '@/components/Dashboard/StatBox';
import BreakdownPanel from '@/components/Dashboard/BreakdownPanel';
import Assets  from '@/services/Assets/Assets';
import DonutChartPanel from '@/components/Dashboard/DonutChartPanel';

export default {
  name: 'AssetsDashboard',
  components: {
    StatBox,
    BreakdownPanel,
    DonutChartPanel,
  },
  data() {
    return {
      loading: false,
      data: {},
      enabledTypes: [],
      assetTypeColours: [
        '#34668C',
        '#FFA600',
        '#477A71',
        '#638BC2',
        '#678DA9',
        '#E87D5C',
        '#c596de',
        '#9bcae5',
        '#9eb3c5',
        '#eeac0a',
        '#2b5fcb',
      ],
      parentAssetTypes: ['door', 'alarms'],
      chartOptions: {},
      selectedTenure: null,
    }
  },
  computed: {
    totalAssets() {
      if (!this.data.counts) {
        return 0;
      }

      let total = 0;
      for (const type in this.data.counts) {
        if (this.enabledTypes.includes(type)) {
          total = total + this.data.counts[type];
        }
      }

      return total;
    },
    breakdownPanelData() {
      if (!this.data.counts) {
        return [];
      }
      const breakdown = [];
      let i = 0;
      for (const type in this.data.counts) {
        if (this.data.counts[type]) {
          breakdown.push({
            label: this.getAssetTypeName(type),
            value: this.data.counts[type],
            bgColor: this.assetTypeColours[i],
            checkboxValue: type,
          });
        }
        i++;
      }

      return breakdown;
    },
    chartData() {
      if (this.loading) {
        return [];
      }
      if (!this.data.counts) {
        return [];
      }

      let series = {
        data: [],
      };

      for (const type in this.data.counts) {
        if (this.enabledTypes.includes(type) && this.data.counts[type]) {
          const label = this.getAssetTypeName(type);
          series.data.push({
            name: this.uppercaseFirst(label),
            label,
            count: this.data.counts[type],
          });
        }
      }

      const data = [
        {
          title: 'All Assets',
          chartData: {
            title: 'All Assets',
            series,
          },
          loading: false,
          containerClass: 'lg',
          parentContainerClass: 'mr-5',
          enableCentreTotal: true,
          ref: 'all',
        },
      ];

      // do breakdown
      for (const level in this.data.breakdown) {
        let series = {
          data: [],
        };

        for (const type in this.data.breakdown[level]) {
          if (this.enabledTypes.includes(type) && this.data.counts[type]) {
            const label = this.getAssetTypeName(type);
            series.data.push({
              name: this.uppercaseFirst(label),
              label,
              count: this.data.breakdown[level][type],
            });
          }
        }

        data.push({
          title: this.uppercaseFirst(level),
          chartData: {
            title: this.uppercaseFirst(level),
            series,
          },
          loading: false,
          containerClass: '',
          parentContainerClass: 'ml-3',
          enableCentreTotal: true,
          ref: level,
        });
      }

      return data;
    },
    chartColors() {
      if (this.loading) {
        return {};
      }

      if (!this.data.counts) {
        return {};
      }

      const colours = {};
      let i = 0;
      for (const type in this.data.counts) {
        const key = this.getAssetTypeName(type);
        colours[key] = this.assetTypeColours[i];
        i++;
      }
      return colours;
    },
  },
  created() {

  },
  async mounted() {
    this.selectedTenure = 'All Tenures';
  },
  watch: {
    '$store.getters.propertyTenure': async function (value) {
      if (value && value !== this.selectedTenure) {
        this.selectedTenure = value;
      }
    },
    selectedTenure: async function (value) {
        await this.fetchData();
    },
    data: {
      handler(newValue) {
        this.enableAllAssetTypes();
        this.loading = false;
      },
    },
    enabledTypes: {
      handler(newValue) {
        if (!this.$refs.chartPanel) {
          return;
        }
        this.$nextTick(() => {
          this.$refs.chartPanel.$refs.all[0].render();
          this.$refs.chartPanel.$refs['level 1'][0].render();
          this.$refs.chartPanel.$refs['level 2'][0].render();
          this.$refs.chartPanel.$refs['level 3'][0].render();
        });
      },
    },
  },
  methods: {
    async fetchData() {
      if (!this.$store.getters.propertyTenure) {
      }
      this.loading = true;

      try {
        await this.getCounts();
      } catch (e) {
        this.$notify({
          title: 'Error',
          text: 'Something went wrong. Please try again later.',
          type: 'error',
        });
      }
    },
    async getCounts() {
      const { data } = await Assets.getCounts(this.selectedTenure, true);
      this.data = this.mergeLevel3(data.data);
    },
    mergeLevel3(data) {
      const { breakdown } = data;
      const newData = {};
      const level3Data = {};

      for (const level in breakdown) {
        // Anything level 1 or level 2 copy as is
        if (level === 'level 1' || level === 'level 2') {
          newData[level] = data.breakdown[level];
          continue;
        }

        // Anything level 3 onwards gets merged together
        for (const type in breakdown[level]) {
          let count = (level3Data[type]) ? level3Data[type] : 0;
          level3Data[type] = count + breakdown[level][type];
        }
      }
      newData['level 3'] = level3Data;

      data.breakdown = newData;

      return data;
    },
    enableAllAssetTypes()
    {
      for (const type in this.data.counts) {
        if (this.data.counts[type] > 0) {
          this.enabledTypes.push(type);
        }
      }
    },
    updateEnabledTypes(data) {
      this.enabledTypes = data;
    },
    uppercaseFirst(string) {
      const arr = string.split(" ");
      for (let i = 0; i < arr.length; i++) {
        arr[i] = arr[i].charAt(0)
            .toUpperCase() + arr[i].slice(1);
      }

      return arr.join(" ");
    },
    getAssetTypeName(type) {
      if (this.parentAssetTypes.includes(type)) {
        return 'other ' + type;
      }

      return type;
    }
  }
};
</script>

<style scoped>

</style>
